import clsx from "clsx";
import { FC, useState } from "react";

import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { ImportSectionRequestType } from "components/server-types";
import { t } from "i18n/i18n";
import { CancelSaveWidget } from "mds/components/CancelSaveWidget";
import { Checkbox } from "mds/components/Checkbox";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { BookSectionIcon, ChevronDownIcon, ChevronUpIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectSectionsByTermAndCourse } from "store/selectors";
import { useListSelector } from "store/store-hooks";

export interface ImportSectionModalProps {
  onCancel: () => void | undefined;
  onConfirm: (data: ImportSectionRequestType) => void | undefined;
}

export const ImportSectionModal: FC<ImportSectionModalProps> = ({ onCancel, onConfirm }) => {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const [selectedTermId, setSelectedTermId] = useState<string>("");
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const showSections = selectedTermId !== "" && selectedCourseId !== "";

  const sections = useListSelector((s) =>
    selectSectionsByTermAndCourse(s, selectedTermId, selectedCourseId),
  );

  interface ContentOptionProps {
    label: "topics" | "assignments" | "course_resources" | "instructor_workspace";
    selected: boolean;
  }

  enum ContentOption {
    TOPICS,
    ASSIGNMENTS,
    SECTION_RESOURCES,
    INSTRUCTOR_WORKSPACE,
  }

  const [contentOptions, setContentOptions] = useState<ContentOptionProps[]>([
    { label: "topics", selected: true },
    { label: "assignments", selected: true },
    { label: "course_resources", selected: true },
    { label: "instructor_workspace", selected: true },
  ]);
  const allOptionsSelected = contentOptions.every((option) => option.selected);
  const noOptionsSelected = contentOptions.every((option) => !option.selected);

  const handleCourseChange = (newCourseId: string) => {
    setSelectedCourseId(newCourseId);
    setSelectedSectionId(null);
  };

  const handleTermChange = (newTermId: string) => {
    setSelectedTermId(newTermId);
    setSelectedSectionId(null);
  };

  const handleSectionChange = (newSectionId: string) => {
    setSelectedSectionId(newSectionId);
  };

  const handleContentOptionsChange = (index) => {
    //   set the selected option to the opposite of what it currently is
    const newOptions = contentOptions.map((option, i) => {
      if (i === index) {
        return { ...option, selected: !option.selected };
      }

      return option;
    });

    // Update the state with the new options array
    setContentOptions(newOptions);
  };

  const handleAllContentOptionsChange = () => {
    setContentOptions(
      contentOptions.map((option) => ({ ...option, selected: !allOptionsSelected })),
    );
  };

  const toggleShowOptions = () => {
    setShowOptions(!showOptions);
  };

  const eligibleSections = sections.filter((section) => section.id !== currentSectionId);
  const hasEligibleSections = eligibleSections.length > 0;

  // TODO: when the template nav is clicked update the list of sections and set the tab to active
  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        <TermMenu
          className="w-full min-w-0"
          selectedTermId={selectedTermId}
          onTermChange={handleTermChange}
        />

        <CourseMenu
          className="w-full min-w-0"
          selectedCourseId={selectedCourseId}
          onCourseChange={handleCourseChange}
        />
      </div>

      <div
        className={clsx(
          "mb-2 mt-4 max-h-[150px] overflow-y-auto rounded-lg border border-black-tint-90 p-2",
          { hidden: !showSections },
        )}
      >
        {hasEligibleSections ? (
          eligibleSections.map((section) => (
            <div key={section.id}>
              <ListItem
                className="min-w-0"
                isSelected={selectedSectionId === section.id}
                onClick={() => handleSectionChange(section.id)}
              >
                <IconText
                  className="w-full"
                  iconStart={<BookSectionIcon />}
                  text={<div className="min-w-0 truncate">{section.title}</div>}
                />
              </ListItem>
            </div>
          ))
        ) : (
          <div className="text-black-tint-55">{t("home.settings.no_sections")}</div>
        )}
      </div>

      <div className={clsx("p-2", { hidden: !showSections || !hasEligibleSections })}>
        <div className="flex items-center gap-2 pb-1">
          <Checkbox checked={allOptionsSelected} onChange={() => handleAllContentOptionsChange()}>
            <span>{t("import_modal.include")}</span>
          </Checkbox>

          <SquareIconButton
            aria-label={
              showOptions ? t("import_modal.hide_options") : t("import_modal.show_options")
            }
            kind="ghost"
            size="s"
            onClick={toggleShowOptions}
          >
            {showOptions ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </SquareIconButton>
        </div>

        {showOptions &&
          contentOptions.map((option: ContentOptionProps, index) => (
            <div className="ml-4 flex gap-2 pb-1" key={option.label}>
              <Checkbox
                checked={option.selected}
                onChange={() => {
                  handleContentOptionsChange(index);
                }}
              >
                <span>{t(`glossary.${option.label}`)}</span>
              </Checkbox>
            </div>
          ))}
      </div>

      <CancelSaveWidget
        className="mt-8 w-full justify-end"
        isSaveDisabled={selectedSectionId === null || noOptionsSelected}
        saveTextKey="import_section"
        size="m"
        onClickCancel={onCancel}
        onClickSave={() => {
          if (selectedSectionId != null && currentSectionId != null) {
            onConfirm?.({
              source_section_id: selectedSectionId,
              target_section_id: currentSectionId,
              include_topics: contentOptions[ContentOption.TOPICS].selected,
              include_assignments: contentOptions[ContentOption.ASSIGNMENTS].selected,
              include_course_resources: contentOptions[ContentOption.SECTION_RESOURCES].selected,
              include_workspace: contentOptions[ContentOption.INSTRUCTOR_WORKSPACE].selected,
            });
          }
        }}
      />
    </div>
  );
};
